import React, { useRef, useEffect, useState } from 'react';
import BuildingMapWorkOrdersModal from "../../../../components/modals/BuildingMapWorkOrders"

interface BuildingWorkOrderCount {
  buildingName: string;
  openWorkOrderCount: number;
  priority: number; 
  workOrderIds: string[];
}

interface BuildingMapProps {
  imageSrc: string;
  buildings: BuildingWorkOrderCount[];
  originalWidth: number;
  originalHeight: number;
}

interface Coordinate {
  x: number;
  y: number;
}

const BuildingMap: React.FC<BuildingMapProps> = ({
  imageSrc,
  buildings,
  originalWidth,
  originalHeight,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredBuilding, setHoveredBuilding] = useState<string | null>(null);

  const scaleCoordinates = (
    coordinates: Record<string, Coordinate>,
    targetWidth: number,
    targetHeight: number
  ): Record<string, Coordinate> => {
    const scaleX = targetWidth / 800; 
    const scaleY = targetHeight / 600;

    const scaled: Record<string, Coordinate> = {};

    Object.entries(coordinates).forEach(([name, { x, y }]) => {
      scaled[name] = {
        x: x * scaleX,
        y: y * scaleY,
      };
    });

    return scaled;
  };


  const nativeCoordinates: Record<string, { x: number; y: number }> = {
  '18 Union Drive': { x: 600, y: 425 },
  'APT 45B 100 UN PLAZA': { x: 600, y: 130 },
  '40-42 Sarles Street': { x: 200, y: 130 },
  '42 Comer Street': { x: 200, y: 425 },
  
  
};

// Scale them based on the props
    const buildingCoordinates = scaleCoordinates(nativeCoordinates, originalWidth, originalHeight);

    


  const buildingsWithCoordinates = buildings
    .map((b) => ({
      ...b,
      ...buildingCoordinates[b.buildingName],
    }))
    .filter((b) => b.x !== undefined && b.y !== undefined);

  useEffect(() => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const container = canvas.parentElement;
  if (container) {
    const scale = container.offsetWidth / originalWidth;
    canvas.style.width = `${container.offsetWidth}px`;
    canvas.style.height = `${originalHeight * scale}px`;
  }
}, []); 

useEffect(() => {

  const canvas = canvasRef.current;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  canvas.width = originalWidth;
  canvas.height = originalHeight;

  const img = new Image();
  img.onload = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, originalWidth, originalHeight);

    const grouped: Record<string, BuildingWorkOrderCount[]> = {};
    buildingsWithCoordinates.forEach((b) => {
      if (!grouped[b.buildingName]) grouped[b.buildingName] = [];
      grouped[b.buildingName].push(b);
    });
    const scaleX = canvas.width / originalWidth;
    const scaleY = canvas.height / originalHeight;

    Object.entries(grouped).forEach(([name, pins]) => {
      const baseX = pins[0].x;
      const baseY = pins[0].y;
      pins.sort((a, b) => a.priority - b.priority);

      pins.forEach((pin, idx) => {
        const offset = (idx - (pins.length - 1) / 2) * 30;

        const pinX = (baseX + offset) * scaleX;
        const pinY = baseY * scaleY;

        const color = pin.priority === 3 ? '#ff0000' : '#ff8800';

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(pinX, pinY, 12, 0, 2 * Math.PI);
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(pin.openWorkOrderCount.toString(), pinX, pinY);

        ctx.beginPath();
        ctx.moveTo(pinX, pinY + 12);
        ctx.lineTo(pinX, pinY + 25);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    });
  };
  img.src = imageSrc as unknown as string;
}, [buildingsWithCoordinates, imageSrc, originalWidth, originalHeight]);




  const handleMouseMove = (e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    let hovered: string | null = null;

    buildingsWithCoordinates.forEach((b) => {
      const offsetX = b.priority === 3 ? 15 : -15;
      const distance = Math.sqrt(
        Math.pow(b.x + offsetX - x, 2) + Math.pow(b.y - y, 2)
      );
      if (distance < 15) hovered = `${b.buildingName}-${b.priority}`;
    });

    setHoveredBuilding(hovered);
    canvas.style.cursor = hovered ? 'pointer' : 'default';
  };

  const handleClick = (e: React.MouseEvent) => {
  const canvas = canvasRef.current;
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  const x = (e.clientX - rect.left) * scaleX;
  const y = (e.clientY - rect.top) * scaleY;

  buildingsWithCoordinates.forEach((b) => {
    const offsetX = b.priority === 3 ? 15 : -15;
    const distance = Math.sqrt(Math.pow(b.x + offsetX - x, 2) + Math.pow(b.y - y, 2));
    if (distance < 15) {
      setSelectedWorkOrderIds(b.workOrderIds);
      setModalOpen(true);
    }
  });
};

  return (
    <div
    >
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        style={{
          display: 'block',
          border: '2px solid #ccc',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        }}
      />
      <BuildingMapWorkOrdersModal
      workOrderIds={selectedWorkOrderIds}
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
    />
    </div>

    
  );
};

export default BuildingMap;
